export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

let cssColorResolverElement = null;

const getCssColorResolverElement = () => {
  if (typeof document === 'undefined') return null;

  if (cssColorResolverElement?.isConnected) {
    return cssColorResolverElement;
  }

  const element = document.createElement('span');
  element.setAttribute('aria-hidden', 'true');
  Object.assign(element.style, {
    position: 'absolute',
    width: '0',
    height: '0',
    inset: '-9999px',
    visibility: 'hidden',
    pointerEvents: 'none',
    overflow: 'hidden',
    padding: '0',
    border: '0',
  });
  document.documentElement.appendChild(element);
  cssColorResolverElement = element;
  return element;
};

const resolveCssColorValue = (input) => {
  if (typeof input !== 'string') return '';

  const normalized = input.trim();
  if (!normalized) return '';
  if (normalized.startsWith('#') || /^rgba?\(/i.test(normalized)) {
    return normalized;
  }
  if (normalized.toLowerCase() === 'transparent') {
    return 'rgba(0, 0, 0, 0)';
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return normalized;
  }

  try {
    const element = getCssColorResolverElement();
    if (!element) return normalized;
    element.style.color = '';
    element.style.color = normalized;
    const computed = window.getComputedStyle(element).color;
    return computed || normalized;
  } catch (error) {
    return normalized;
  }
};

export const parseColorToRgb = (input) => {
  const color = resolveCssColorValue(input);
  if (!color) return null;

  if (color.startsWith('#')) {
    if (color.length === 4) {
      const r = parseInt(color[1] + color[1], 16);
      const g = parseInt(color[2] + color[2], 16);
      const b = parseInt(color[3] + color[3], 16);
      return [r, g, b].every(Number.isFinite) ? [r, g, b] : null;
    }

    if (color.length === 7) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return [r, g, b].every(Number.isFinite) ? [r, g, b] : null;
    }
  }

  const rgbMatch = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+)\s*)?\)$/i
  );
  if (!rgbMatch) return null;

  const toChannel = (value) => clamp(Math.round(Number(value)), 0, 255);
  return [toChannel(rgbMatch[1]), toChannel(rgbMatch[2]), toChannel(rgbMatch[3])];
};

export const colorWithAlpha = (color, alpha) => {
  const safeAlpha = clamp(Number(alpha), 0, 1);
  const rgb = parseColorToRgb(color);
  if (!rgb) return color;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${safeAlpha})`;
};

export const mixColors = (colorA, colorB, weight = 0.5) => {
  const rgbA = parseColorToRgb(colorA);
  const rgbB = parseColorToRgb(colorB);
  if (!rgbA && !rgbB) return colorA || colorB || 'var(--icon-reset-password)';
  if (!rgbA) return colorB;
  if (!rgbB) return colorA;

  const factor = clamp(Number(weight), 0, 1);
  const result = rgbA.map((channel, index) =>
    Math.round(channel + (rgbB[index] - channel) * factor)
  );
  return `rgb(${result[0]}, ${result[1]}, ${result[2]})`;
};

export const getColorLuminance = (color) => {
  const rgb = parseColorToRgb(color);
  if (!rgb) return 0;

  const channels = rgb.map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

export const pickReadableColor = (
  backgroundColor,
  lightColor = 'var(--part-color)',
  darkColor = 'var(--widget-title-color)'
) => (getColorLuminance(backgroundColor) > 0.58 ? darkColor : lightColor);

const interpolateColor = (colorA, colorB, factor) => mixColors(colorA, colorB, factor);

const normalizeStops = (stops) => {
  if (!Array.isArray(stops)) return [];

  return stops
    .filter((stop) => stop && Number.isFinite(Number(stop.value)) && stop.color)
    .map((stop) => ({
      ...stop,
      value: Number(stop.value),
    }))
    .sort((left, right) => left.value - right.value);
};

export const resolveValueColorFromStops = ({
  value,
  baseColor,
  stops = [],
  enabled = false,
  grading = false,
}) => {
  const fixedColor =
    typeof baseColor === 'string' && baseColor.trim()
      ? baseColor
      : baseColor || null;

  if (!enabled) return fixedColor;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fixedColor;

  const normalizedStops = normalizeStops(stops);
  if (normalizedStops.length === 0) return fixedColor;

  if (numericValue <= normalizedStops[0].value) return normalizedStops[0].color;
  if (numericValue >= normalizedStops[normalizedStops.length - 1].value) {
    return normalizedStops[normalizedStops.length - 1].color;
  }

  for (let index = 0; index < normalizedStops.length - 1; index += 1) {
    const lower = normalizedStops[index];
    const upper = normalizedStops[index + 1];
    if (numericValue < lower.value || numericValue > upper.value) continue;

    if (!grading) {
      return upper.color;
    }

    const range = upper.value - lower.value;
    const ratio = range > 0 ? (numericValue - lower.value) / range : 0;
    return interpolateColor(lower.color, upper.color, ratio);
  }

  return normalizedStops[normalizedStops.length - 1].color;
};

export const resolveLiquidColor = ({
  color,
  value,
  min,
  max,
  changeColorBasedOnValue,
  colorGrading,
  colors,
}) => {
  const fixedColor = String(color || '').trim() || 'var(--icon-reset-password)';
  return resolveValueColorFromStops({
    value,
    baseColor: fixedColor,
    stops: colors,
    enabled: changeColorBasedOnValue,
    grading: colorGrading,
  }) || fixedColor;
};

export const buildWavePath = ({
  amplitude,
  baseline,
  width = 220,
  segmentWidth = 28,
  height = 150,
  invert = false,
}) => {
  const startX = -50;
  const segmentCount = Math.ceil(width / segmentWidth) + 4;
  let path = `M ${startX} ${baseline}`;

  for (let index = 0; index < segmentCount; index += 1) {
    const x = startX + index * segmentWidth;
    const nextX = x + segmentWidth;
    const controlX = x + segmentWidth / 2;
    const direction = (index + (invert ? 1 : 0)) % 2 === 0 ? -1 : 1;
    const controlY = baseline + direction * amplitude;
    path += ` Q ${controlX} ${controlY} ${nextX} ${baseline}`;
  }

  path += ` L ${startX + segmentCount * segmentWidth} ${height}`;
  path += ` L ${startX} ${height} Z`;
  return path;
};

export const formatDisplayValue = (value, decimals) => {
  if (!Number.isFinite(value)) return '—';
  const parsedDecimals = Number(decimals);
  if (Number.isFinite(parsedDecimals)) {
    return Number(value.toFixed(Math.max(0, parsedDecimals))).toString();
  }
  return Number.isInteger(value) ? String(value) : Number(value.toFixed(1)).toString();
};
